import { modTypes, transformChangeTypes } from "@blings/blings-player";
import type { IModModel } from "../../../stores/ModsStore";
import type { LayerInspectorSelection } from "../../../helpers/layerInspectorTools";
import type {
  OpenVisualPropertyConnectorOptions,
  VisualEditableField,
} from "./types";
import { getPendingFieldPath } from "./utils";

type ConnectorModData = Partial<IModModel["moddata"]> & Record<string, unknown>;

type ConnectorTarget = {
  assetId?: string;
  layerName: string;
  layerUid?: number;
};

const COLOR_PROPERTIES = new Set([
  "fillColor",
  "textFillColor",
  "solidColor",
  "strokeColor",
  "textStrokeColor",
]);

const TRANSFORM_PROPERTY_TYPES: Partial<Record<
  string,
  transformChangeTypes
>> = {
  position: transformChangeTypes.positionX,
  rotation: transformChangeTypes.rotation,
  scale: transformChangeTypes.scale,
};

const getPrimaryField = (
  fields: Array<VisualEditableField | undefined>
): VisualEditableField | undefined => fields.find(Boolean);

export const getConnectorTargetFields = (
  fields: Array<VisualEditableField | undefined>
): VisualEditableField[] => {
  const primaryField = getPrimaryField(fields);

  if (primaryField?.targetFields?.length) {
    return primaryField.targetFields;
  }

  return fields.filter((field): field is VisualEditableField =>
    Boolean(field?.layerName)
  );
};

const normalizeAssetId = (
  assetId: string | undefined,
  mainAssetKey: string | undefined
) => (assetId && assetId !== mainAssetKey ? assetId : undefined);

const fieldToConnectorTarget = (
  field: VisualEditableField,
  mainAssetKey?: string
): ConnectorTarget | undefined => {
  if (!field.layerName) {
    return undefined;
  }

  return {
    assetId: normalizeAssetId(field.assetId, mainAssetKey),
    layerName: field.layerName,
    layerUid: field.layerUid,
  };
};

export const getConnectorTargetsForFields = (
  fields: Array<VisualEditableField | undefined>,
  mainAssetKey?: string
): ConnectorTarget[] => {
  const seen = new Set<string>();

  return getConnectorTargetFields(fields).reduce<ConnectorTarget[]>(
    (targets, field) => {
      const target = fieldToConnectorTarget(field, mainAssetKey);
      if (!target) return targets;

      const key = `${target.assetId ?? mainAssetKey ?? "main"}::${
        target.layerUid ?? ""
      }::${target.layerName}`;
      if (seen.has(key)) return targets;

      seen.add(key);
      targets.push(target);
      return targets;
    },
    []
  );
};

const applyConnectorTargets = (
  modData: ConnectorModData,
  targets: ConnectorTarget[]
) => {
  const [primaryTarget, ...additionalTargets] = targets;
  if (!primaryTarget) return modData;

  return {
    ...modData,
    ...primaryTarget,
    ...(additionalTargets.length ? { additionals: additionalTargets } : {}),
  };
};

const getTransformChangeType = (field: VisualEditableField | undefined) =>
  field ? TRANSFORM_PROPERTY_TYPES[field.property] : undefined;

const getTransformChangeTypeForComponent = (
  field: VisualEditableField | undefined,
  componentIndex: number | undefined
) => {
  if (!field) return undefined;

  if (field.property === "position") {
    if (componentIndex === undefined || componentIndex === 0) {
      return transformChangeTypes.positionX;
    }

    return componentIndex === 1 ? transformChangeTypes.positionY : undefined;
  }

  if (field.property === "scale") {
    if (componentIndex === undefined) return transformChangeTypes.scale;
    if (componentIndex === 0) return transformChangeTypes.scaleX;
    if (componentIndex === 1) return transformChangeTypes.scaleY;
    return undefined;
  }

  return getTransformChangeType(field);
};

const isColorField = (field: VisualEditableField | undefined) =>
  Boolean(
    field &&
      (field.valueType === "color" || COLOR_PROPERTIES.has(field.property))
  );

const getColorConnectorFroms = (
  field: VisualEditableField | undefined
): unknown[] => {
  const value = field?.value;

  if (typeof value === "string" && value.trim()) {
    return [value];
  }

  if (
    Array.isArray(value) &&
    value.length >= 3 &&
    value.every((entry) => typeof entry === "number" && Number.isFinite(entry))
  ) {
    return [value.slice()];
  }

  return [];
};

const getSelectedColorConnectorFroms = (selectedColor: string | undefined) =>
  selectedColor ? [selectedColor] : [];

const normalizeConnectorPath = (path: string) =>
  path.replace(/\[(\d+)\]/g, ".$1").replace(/^\./, "");

const shouldUseAnimatedValuePath = (field: VisualEditableField, path: string) =>
  (field.valueType === "number" || field.valueType === "vector") &&
  !/^t\.d\.k\.\d+\.s\./.test(path) &&
  !/(^|\.)k$/.test(path);

const getCustomValuePath = (
  field: VisualEditableField,
  path: string,
  componentIndex: number | undefined
) => {
  const normalizedPath = normalizeConnectorPath(path);
  if (!normalizedPath) {
    return "";
  }

  const valuePath = shouldUseAnimatedValuePath(field, normalizedPath)
    ? `${normalizedPath}.k`
    : normalizedPath;

  if (
    componentIndex === undefined ||
    !Number.isInteger(componentIndex) ||
    componentIndex < 0 ||
    field.valueType !== "vector"
  ) {
    return valuePath;
  }

  return `${valuePath}.${componentIndex}`;
};

const getCustomPath = (
  field: VisualEditableField | undefined,
  componentIndex?: number
) => {
  if (!field) return "";

  const path = getPendingFieldPath(field) ?? field.rootPath ?? field.path ?? "";
  return getCustomValuePath(field, path, componentIndex);
};

export const buildPropertyConnectorModData = ({
  fields,
  mainAssetKey,
  componentIndex,
  preferredType,
  selectedColor,
}: {
  fields: Array<VisualEditableField | undefined>;
  mainAssetKey?: string;
} & OpenVisualPropertyConnectorOptions): ConnectorModData => {
  const primaryField = getPrimaryField(fields);
  const targets = getConnectorTargetsForFields(fields, mainAssetKey);

  if (preferredType === "media") {
    return { type: modTypes.asset, imageName: "" };
  }

  if (preferredType === "documentColor") {
    return {
      type: modTypes.themeColor,
      froms: getSelectedColorConnectorFroms(selectedColor),
      to: "",
      isLayers: false,
    };
  }

  if (isColorField(primaryField)) {
    return applyConnectorTargets(
      {
        type: modTypes.themeColor,
        froms: getColorConnectorFroms(primaryField),
        to: "",
        isLayers: true,
      },
      targets
    );
  }

  if (primaryField?.property === "text") {
    return applyConnectorTargets(
      {
        type: modTypes.text,
        maxChars: undefined,
        verticalAlign: undefined,
        fontSize: undefined,
        center: false,
        dontTrim: false,
        caseAdjustment: "",
        exposeToPlatform: true,
      },
      targets
    );
  }

  const transformChangeType = getTransformChangeTypeForComponent(
    primaryField,
    componentIndex
  );
  if (transformChangeType) {
    return applyConnectorTargets(
      {
        type: modTypes.transform,
        isAbsolute: true,
        transformChangeType,
      },
      targets
    );
  }

  return applyConnectorTargets(
    {
      type: modTypes.custom,
      path: getCustomPath(primaryField, componentIndex),
      nvType: "string",
    },
    targets
  );
};

export const getFallbackConnectorSelections = (
  fields: Array<VisualEditableField | undefined>,
  fallbackSelections: LayerInspectorSelection[],
  mainAssetKey?: string
): LayerInspectorSelection[] => {
  if (fallbackSelections.length) {
    return fallbackSelections;
  }

  return getConnectorTargetsForFields(fields, mainAssetKey).map((target) => ({
    layerName: target.layerName,
    layerUid: target.layerUid,
    assetName: target.assetId,
    path: "",
  }));
};
